import { ResponseStatuses, OrderCartFields } from '@/enums';
import { SharedCartOrganization } from '@/modules/cart/shared/types';
import { Cart } from '@/modules/cart/types';
import { AxiosResponse } from 'axios';
import { Actions, Context } from 'vuex-smart-module';
import CartStore from '@/modules/cart/store';
import { Store } from 'vuex';
import CartMutations from '@/modules/cart/store/mutations';
import { AlfaApiLoanApplicationService } from '@/modules/alfa-api-loan-applications/services';
import { TableRequest } from '@/types';
import { OrdersService } from '../services';
import { Orders } from '../types';
import OrderGetters from './getters';
import OrderMutations from './mutations';
import OrderState from './state';

const ordersService = new OrdersService();
const alfaApiLoanApplicationService = new AlfaApiLoanApplicationService();

export default class OrderActions extends Actions<
  OrderState,
  OrderGetters,
  OrderMutations,
  OrderActions
> {
  cart_store!: Context<typeof CartStore>;
  $init(store: Store<CartMutations>): void {
    this.cart_store = CartStore.context(store);
  }

  set_new_order(order: Orders.Order) {
    this.mutations.SET_NEW_ORDER(order);
  }

  update_new_order({ key, value }: { key: keyof Orders.Order; value: unknown }) {
    this.mutations.UPD_NEW_ORDER_FIELD({ key, value });
  }

  upd_order_product(items: Orders.UpdateOrderDTO | Orders.UpdateOrderDTO[]) {
    this.mutations.UPD_ORDER_PRODUCT(items);
  }

  set_products_list(products_list: Cart.CartItem[]) {
    this.mutations.SET_ORDER_PRODUCTS_LIST(products_list);
  }

  async send_order() {
    this.mutations.UPDATE_NOTIFICATION_LOADING(true);

    try {
      const { data, code } = await ordersService.send_order(this.state.new_order);

      localStorage.removeItem(OrderCartFields.ORDER_CART);

      this.mutations.SET_CREATED_ORDER(data);

      return code;
    } catch (error) {
      console.log(error);

      this.cart_store.commit('SET_ITEMS_WITHOUT_ACCREDITATION', (error as AxiosResponse).data.items_without_accreditation as Cart.CartItem[]);
      this.cart_store.commit('SET_ACCREDITATION_POPUP_MESSAGE', (error as AxiosResponse).data.message as string | null);
    } finally {
      this.mutations.UPDATE_NOTIFICATION_LOADING(false);
    }
  }

  async get_info_from_last_order() {
    try {
      const { data } = await ordersService.get_info_from_last_order();

      this.mutations.SET_LAST_ORDER_INFO(data);
    } catch (error) {
      console.log(error);
    }
  }

  set_order_organizations(orgs: SharedCartOrganization[]) {
    this.mutations.SET_ORDER_ORGANIZATION(orgs);
  }

  async get_orders(storage_limit?: number) {
    this.mutations.SET_LOADING(true);

    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;

      const filtersEmpty = this.state.organizations.length < 1;

      const payload: TableRequest<Orders.RequestFilters> = {
        filters: this.state.filters,
        filters_exist: !filtersEmpty,
        pagination: {
          limit: storage_limit ?? itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await ordersService.get_orders(payload);

      this.mutations.SET_ORDERS(data.items);
      this.mutations.SET_ADDITIONAL(data.additional);

      if (filtersEmpty) {
        this.mutations.SET_FILTERS(data.filters);
      }

      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.SET_LOADING(false);
    }
  }

  async get_order_by_id(id: string | number) {
    this.mutations.SET_ORDER_LOADING(true);

    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await ordersService.get_order_by_id(
        id,
      );

      this.mutations.SET_ORDER_DETAILS(data);
      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    } finally {
      this.mutations.SET_ORDER_LOADING(false);
    }
  }

  async get_order_documents(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await ordersService.get_order_documents(
        id,
      );

      this.mutations.SET_ORDER_DOCUMENTS(data);
      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async get_order_history_logs(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await ordersService.get_order_history_logs(
        id,
      );

      this.mutations.SET_ORDER_HISTORY_LOGS(data);
      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  // обновление данных о заказе на сервере

  async update_order(params: { id: number; updating_items: Orders.UpdatedOrderDetails[] }) {
    const { id, updating_items } = params;

    const payload = { items: updating_items };

    try {
      const { code } = await ordersService.update_order_by_id(
        id,
        payload,
      );

      console.log(code);

      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async get_cancel_reasons() {
    try {
      const { data } = await ordersService.get_cancel_reasons();

      this.mutations.SET_CANCEL_REASONS(data.items);
      return data;
    } catch (error) {
      console.error(error);
    }
  }

  async cancel_order(id: string | number) {
    try {
      const order_cancel_reason_id = this.state.cancel_order_dialog.cancel_reason_id;

      await ordersService.cancel_order(id, order_cancel_reason_id);

      this.mutations.CLEAR_CANCEL_REASON_DIALOG();
    } catch (error) {
      console.error(error);
    }
  }

  async get_information_on_order_shipment(products: Orders.OrderItemWithoutDate[]) {
    const payload = {
      products,
    };

    try {
      const { data } = await ordersService.get_information_on_order_shipment(payload);

      this.mutations.SET_SHIPMENT_INFORMATION(data.items);
    } catch (error) {
      console.error(error);
    }
  }

  async create_loan_application(id: string | number) {
    return alfaApiLoanApplicationService.store(id);
  }

  async post_information_on_order_shipment() {
    const { shipment_orders_date } = this.state;
    const payload = {
      items: shipment_orders_date,
    };

    try {
      const { code } = await ordersService.post_information_on_order_shipment(payload);

      return code;
    } catch (error) {
      console.error(error);

      if ((error as AxiosResponse).status === ResponseStatuses.Fail.CONFLICT) {
        this.mutations.SET_SHIPMENT_CONFIRM_ERROR(true);
        this.mutations.SET_SHIPMENT_CONFIRM_ERROR_MESSAGE((error as AxiosResponse).data.message);
      }

      return (error as AxiosResponse).data.code;
    }
  }
}
